import {
  isLikelyLocalFilesystemPath,
  requiresNativeURLResolution,
  resolveAttachmentDisplayURL,
  type AttachmentDisplayURLParams,
} from '@/utils/agent-file-url';

export type AttachmentNativeResolutionOptions = {
  preferDataURL: boolean;
};

export type AttachmentNativeSourceAdapter = {
  resolveAuthenticatedSource: (
    source: string,
    options: AttachmentNativeResolutionOptions
  ) => Promise<string | null | undefined>;
  resolveLocalFileSource?: (path: string) => Promise<string | null | undefined>;
};

export type AttachmentSourceResolution = {
  displaySource: string | null;
  localFallbackUsed: boolean;
  source: string;
};

type AttachmentSourceResolutionFailure = {
  displaySource: string | null;
  error?: unknown;
  reason:
    | 'missing-source'
    | 'native-authenticated-source-failed'
    | 'native-local-source-failed';
};

export type AttachmentSourceResolutionResult =
  | { status: 'resolved'; value: AttachmentSourceResolution }
  | { status: 'unresolved'; failure: AttachmentSourceResolutionFailure };

export type AttachmentSourceResolverRequest = AttachmentDisplayURLParams & {
  preferAuthenticatedDataURL?: boolean;
};

type AttachmentSourceResolverContext = {
  candidateSource?: string;
  displaySource: string | null;
  localSource?: string;
  nativeAdapter?: AttachmentNativeSourceAdapter | null;
  nativeBridgeAvailable: boolean;
  preferAuthenticatedDataURL: boolean;
};

type AttachmentSourceResolver = {
  id: string;
  canResolve: (context: AttachmentSourceResolverContext) => boolean;
  resolve: (
    context: AttachmentSourceResolverContext
  ) => Promise<AttachmentSourceResolutionResult>;
};

function resolved(
  source: string,
  context: AttachmentSourceResolverContext,
  localFallbackUsed = false
): AttachmentSourceResolutionResult {
  return {
    status: 'resolved',
    value: {
      displaySource: context.displaySource,
      localFallbackUsed,
      source,
    },
  };
}

function unresolved(
  reason: AttachmentSourceResolutionFailure['reason'],
  context: AttachmentSourceResolverContext,
  error?: unknown
): AttachmentSourceResolutionResult {
  return {
    status: 'unresolved',
    failure: {
      displaySource: context.displaySource,
      error,
      reason,
    },
  };
}

export function localAttachmentSource(params: {
  src?: string | null;
  filePath?: string | null;
}): string | null {
  const candidateSource = params.src?.trim();
  const localPath = params.filePath?.trim();
  return (
    (candidateSource && isNativeLocalAttachmentPath(candidateSource)
      ? candidateSource
      : null) ??
    (localPath && isNativeLocalAttachmentPath(localPath) ? localPath : null)
  );
}

function isNativeLocalAttachmentPath(value: string): boolean {
  const normalized = value.trim().replace(/\\/g, '/');
  if (
    normalized === '/.willow' ||
    normalized.startsWith('/.willow/') ||
    normalized === '.willow' ||
    normalized.startsWith('.willow/')
  ) {
    return false;
  }
  return isLikelyLocalFilesystemPath(value);
}

async function resolveLocalSource(
  context: AttachmentSourceResolverContext
): Promise<AttachmentSourceResolutionResult> {
  if (!context.localSource || !context.nativeAdapter?.resolveLocalFileSource) {
    return unresolved('missing-source', context);
  }

  try {
    const source = await context.nativeAdapter.resolveLocalFileSource(
      context.localSource
    );
    if (source) {
      return resolved(source, context, true);
    }
    return unresolved('native-local-source-failed', context);
  } catch (error) {
    return unresolved('native-local-source-failed', context, error);
  }
}

const dataURLResolver: AttachmentSourceResolver = {
  id: 'data-url',
  canResolve: context => context.displaySource?.startsWith('data:') ?? false,
  resolve: async context => resolved(context.displaySource ?? '', context),
};

const nativeAuthenticatedResolver: AttachmentSourceResolver = {
  id: 'native-authenticated',
  canResolve: context =>
    Boolean(
      context.displaySource &&
      context.nativeBridgeAvailable &&
      requiresNativeURLResolution(context.displaySource)
    ),
  resolve: async context => {
    const displaySource = context.displaySource;
    if (!displaySource || !context.nativeAdapter) {
      return unresolved('native-authenticated-source-failed', context);
    }

    try {
      const source = await context.nativeAdapter.resolveAuthenticatedSource(
        displaySource,
        {
          preferDataURL: context.preferAuthenticatedDataURL,
        }
      );
      if (source) {
        return resolved(source, context);
      }
    } catch (error) {
      const fallback = await resolveLocalSource(context);
      if (fallback.status === 'resolved') {
        return fallback;
      }
      return unresolved('native-authenticated-source-failed', context, error);
    }

    const fallback = await resolveLocalSource(context);
    if (fallback.status === 'resolved') {
      return fallback;
    }
    return unresolved('native-authenticated-source-failed', context);
  },
};

const browserDisplaySourceResolver: AttachmentSourceResolver = {
  id: 'browser-display-source',
  canResolve: context =>
    Boolean(
      context.displaySource &&
      (!context.nativeBridgeAvailable ||
        !requiresNativeURLResolution(context.displaySource))
    ),
  resolve: async context => resolved(context.displaySource ?? '', context),
};

const nativeLocalSourceResolver: AttachmentSourceResolver = {
  id: 'native-local-source',
  canResolve: context =>
    Boolean(context.localSource && context.nativeBridgeAvailable),
  resolve: resolveLocalSource,
};

const rawCandidateSourceResolver: AttachmentSourceResolver = {
  id: 'raw-candidate-source',
  canResolve: context =>
    Boolean(
      context.candidateSource &&
      !isLikelyLocalFilesystemPath(context.candidateSource)
    ),
  resolve: async context => resolved(context.candidateSource ?? '', context),
};

const attachmentSourceResolvers: AttachmentSourceResolver[] = [
  dataURLResolver,
  nativeAuthenticatedResolver,
  browserDisplaySourceResolver,
  nativeLocalSourceResolver,
  rawCandidateSourceResolver,
];

export async function resolveAttachmentSource(
  request: AttachmentSourceResolverRequest,
  options: {
    nativeAdapter?: AttachmentNativeSourceAdapter | null;
    nativeBridgeAvailable: boolean;
  }
): Promise<AttachmentSourceResolutionResult> {
  const candidateSource = request.src?.trim() || undefined;
  const filePath = request.filePath?.trim() || undefined;
  const displaySource = resolveAttachmentDisplayURL({
    agentId: request.agentId,
    authRequired: request.authRequired,
    auth_required: request.auth_required,
    authScope: request.authScope,
    auth_scope: request.auth_scope,
    displayURL: request.displayURL,
    displayUrl: request.displayUrl,
    display_url: request.display_url,
    downloadURL: request.downloadURL,
    downloadUrl: request.downloadUrl,
    download_url: request.download_url,
    environmentId: request.environmentId,
    filePath,
    fileRefs: request.fileRefs,
    preferSignedAgentFileShare: request.preferSignedAgentFileShare,
    publicShareURL: request.publicShareURL,
    publicShareUrl: request.publicShareUrl,
    public_share_url: request.public_share_url,
    signature: request.signature,
    src: candidateSource,
    thumbnailURL: request.thumbnailURL,
    thumbnailUrl: request.thumbnailUrl,
    thumbnail_url: request.thumbnail_url,
  });
  const context: AttachmentSourceResolverContext = {
    candidateSource,
    displaySource,
    localSource:
      localAttachmentSource({
        filePath,
        src: candidateSource,
      }) ?? undefined,
    nativeAdapter: options.nativeAdapter,
    nativeBridgeAvailable: options.nativeBridgeAvailable,
    preferAuthenticatedDataURL: request.preferAuthenticatedDataURL ?? false,
  };

  for (const resolver of attachmentSourceResolvers) {
    if (!resolver.canResolve(context)) {
      continue;
    }
    return resolver.resolve(context);
  }

  return unresolved('missing-source', context);
}
